import { useCallback, useMemo, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Hook for map / geo / trip endpoints. Designed to be framework-agnostic so a
// future React-Native client can re-use the exact same backend contracts.
// IMPORTANT: returns a STABLE reference (memoized) so consumers can safely place
// it in useEffect/useCallback dependency arrays without triggering infinite
// re-renders. Loading/error are exposed via refs (read with api.loading / api.error)
// which mutate without changing identity.
export function useMapApi() {
  const loadingRef = useRef(false);
  const errorRef = useRef(null);
  const setLoading = (v) => { loadingRef.current = v; };
  const setError = (v) => { errorRef.current = v; };

  const getStoriesInBbox = useCallback(async ({ bbox, category, limit = 500 } = {}) => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (bbox) p.set("bbox", bbox);
      if (category) p.set("category", category);
      if (limit) p.set("limit", String(limit));
      const res = await axios.get(`${API}/map/stories?${p.toString()}`);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return { count: 0, stories: [] };
    } finally { setLoading(false); }
  }, []);

  const getNearby = useCallback(async ({ lat, lng, radius_km = 25, category, limit = 50 }) => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ lat: String(lat), lng: String(lng), radius_km: String(radius_km), limit: String(limit) });
      if (category) p.set("category", category);
      const res = await axios.get(`${API}/map/nearby?${p.toString()}`);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return { count: 0, stories: [] };
    } finally { setLoading(false); }
  }, []);

  const geocode = useCallback(async (q) => {
    if (!q || q.length < 2) return [];
    try {
      const res = await axios.get(`${API}/geocode?q=${encodeURIComponent(q)}`);
      return res.data.results || [];
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return [];
    }
  }, []);

  // Reverse geocode (lat/lng -> address) for the create-story map picker
  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const res = await axios.get(`${API}/geocode/reverse?lat=${lat}&lng=${lng}`);
      return res.data || null;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    }
  }, []);

  // Trips
  const listTrips = useCallback(async () => {
    try { const r = await axios.get(`${API}/trips`); return r.data.trips || []; }
    catch (err) { setError(err.response?.data?.detail || err.message); return []; }
  }, []);

  const createTrip = useCallback(async ({ name, description = "", story_ids = [] }) => {
    try { const r = await axios.post(`${API}/trips`, { name, description, story_ids }); return r.data; }
    catch (err) { setError(err.response?.data?.detail || err.message); return null; }
  }, []);

  const getTrip = useCallback(async (id) => {
    try { const r = await axios.get(`${API}/trips/${id}`); return r.data; }
    catch (err) { setError(err.response?.data?.detail || err.message); return null; }
  }, []);

  const updateTrip = useCallback(async (id, payload) => {
    try { const r = await axios.put(`${API}/trips/${id}`, payload); return r.data; }
    catch (err) { setError(err.response?.data?.detail || err.message); return null; }
  }, []);

  const deleteTrip = useCallback(async (id) => {
    try { await axios.delete(`${API}/trips/${id}`); return true; }
    catch (err) { setError(err.response?.data?.detail || err.message); return false; }
  }, []);

  const addStoryToTrip = useCallback(async (tripId, storyId) => {
    try { const r = await axios.post(`${API}/trips/${tripId}/stories/${storyId}`); return r.data; }
    catch (err) { setError(err.response?.data?.detail || err.message); return null; }
  }, []);

  const removeStoryFromTrip = useCallback(async (tripId, storyId) => {
    try { const r = await axios.delete(`${API}/trips/${tripId}/stories/${storyId}`); return r.data; }
    catch (err) { setError(err.response?.data?.detail || err.message); return null; }
  }, []);

  // Stable, memoized once — all callbacks are stable (deps [])
  return useMemo(() => ({
    get loading() { return loadingRef.current; },
    get error() { return errorRef.current; },
    getStoriesInBbox, getNearby, geocode, reverseGeocode,
    listTrips, createTrip, getTrip, updateTrip, deleteTrip,
    addStoryToTrip, removeStoryFromTrip,
  }), [getStoriesInBbox, getNearby, geocode, reverseGeocode,
       listTrips, createTrip, getTrip, updateTrip, deleteTrip,
       addStoryToTrip, removeStoryFromTrip]);
}

export const CATEGORY_COLORS = {
  "Landmarks":       "#1A2B3C",
  "Hidden Gems":     "#D4A574",
  "Vanished Voices": "#B85C38",
  "Wild Echoes":     "#5C8A4F",
  "City Pulses":     "#7C5CD4",
};

export const CATEGORIES = Object.keys(CATEGORY_COLORS);
